import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { Reveal } from '../components/Reveal';
import { ImageReveal } from '../components/ImageReveal';
import { CTASection } from '../components/CTASection';
import { projects } from '../data/work';
const ease = [0.22, 1, 0.36, 1];
export function CaseStudy() {
  const {
    slug
  } = useParams();
  const index = projects.findIndex(p => p.slug === slug);
  const project = projects[index];
  if (!project) {
    return <section className="px-6 py-48 text-center sm:px-10" data-id="element-207" data-mp="3gjkg4vtuidaz">
        <h1 className="font-serif text-4xl font-light" data-id="element-208" data-mp="32np0cyuqtibt">Case study not found</h1>
        <Link to="/work" className="link-underline mt-8 inline-block text-sm text-charcoal/70" data-id="element-209" data-mp="37ma252pn8a2d">
          Back to all work
        </Link>
      </section>;
  }
  const next = projects[(index + 1) % projects.length];
  return <article data-id="element-210" data-mp="3c50nj9tlfapg">
      <header className="px-6 pb-14 pt-36 sm:px-10 sm:pt-44" data-id="element-211" data-mp="3c4qxjm8jt3jr">
        <div className="mx-auto max-w-[92rem]" data-id="element-212" data-mp="3c2w9hz1zcvbg">
          <Link to="/work" className="link-underline inline-flex items-center gap-2 text-sm text-charcoal/55 hover:text-charcoal" data-id="element-213" data-mp="376pdoz3at078">
            <ArrowLeftIcon className="h-4 w-4" strokeWidth={1.3} data-id="element-214" data-mp="3kz5ctot2we2v" /> All work
          </Link>
          <motion.p initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          ease
        }} className="eyebrow mt-12 text-mocha" data-id="element-215" data-mp="3gy38itkb1bu1">
            {project.category}
          </motion.p>
          <motion.h1 initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.9,
          delay: 0.08,
          ease
        }} className="mt-6 max-w-4xl font-serif text-[2.75rem] font-light leading-[1] tracking-[-0.01em] text-charcoal sm:text-[4.25rem]" data-id="element-216" data-mp="328e50roqovql">
            {project.title}
          </motion.h1>
        </div>
      </header>

      <div className="px-4 sm:px-6" data-id="element-217" data-mp="368z0t67m2sm4">
        <ImageReveal src={project.image} alt={project.title} className="mx-auto aspect-[16/9] max-w-[92rem] rounded-[2rem] border border-charcoal/[0.07]" parallax={30} data-id="element-218" data-mp="3b8k0jiulv4zw" />
      </div>

      <section className="px-6 py-24 sm:px-10 sm:py-32" data-id="element-219" data-mp="3k9i6i6oltml4">
        <div className="mx-auto grid max-w-[92rem] gap-14 lg:grid-cols-12 lg:gap-20" data-id="element-220" data-mp="3k6w0wcl9xh5j">
          <aside className="lg:col-span-3" data-id="element-221" data-mp="2nktzs6ic9lr0">
            <dl className="space-y-8" data-id="element-222" data-mp="2rk2xfmgi23ru">
              {[{
              k: 'Client',
              v: project.client
            }, {
              k: 'Year',
              v: project.year
            }, {
              k: 'Location',
              v: project.location
            }].map(row => <Reveal key={row.k} data-id="element-223" data-mp="31bp2t265p8mo">
                  <div className="border-t border-charcoal/15 pt-4" data-id="element-224" data-mp="23v9w1ah3z3pu">
                    <dt className="eyebrow text-charcoal/40" data-id="element-225" data-mp="23sxhz8eh1di2">{row.k}</dt>
                    <dd className="mt-2 text-sm leading-relaxed text-charcoal/75" data-id="element-226" data-mp="28ss5r32y9myw">
                      {row.v}
                    </dd>
                  </div>
                </Reveal>)}
            </dl>
          </aside>

          <div className="lg:col-span-8 lg:col-start-5" data-id="element-227" data-mp="1w9o87e21zcsh">
            <Reveal data-id="element-228" data-mp="1rb36fa75kl1x">
              <p className="font-serif text-[1.75rem] font-light leading-[1.35] text-charcoal" data-id="element-229" data-mp="1ral2dre912jg">
                {project.excerpt}
              </p>
            </Reveal>

            <Reveal delay={0.08} data-id="element-230" data-mp="1icrhdclxpvbl">
              <h2 className="eyebrow mt-16 text-mocha" data-id="element-231" data-mp="1idsurnqfatwh">The challenge</h2>
              <p className="mt-6 text-[1.0625rem] leading-[1.85] text-charcoal/70" data-id="element-232" data-mp="1dk7utq59rpun">
                {project.challenge}
              </p>
            </Reveal>

            <Reveal delay={0.14} data-id="element-233" data-mp="13n3iei1878r1">
              <h2 className="eyebrow mt-16 text-mocha" data-id="element-234" data-mp="13o4sokaf3r7p">The approach</h2>
            </Reveal>
            <ol className="mt-6 space-y-6" data-id="element-235" data-mp="zk6rm41j86c0">
              {project.approach.map((step, i) => <Reveal key={step} delay={0.06 * i} as="li" data-id="element-236" data-mp="un6ljt4on3kw" data-mpi={i}>
                  <div className="grid grid-cols-[2.5rem_1fr] gap-4 border-t border-charcoal/[0.1] pt-5" data-id="element-237" data-mp="2uod67d9y1bq1">
                    <span className="font-serif text-lg text-charcoal/35" data-id="element-238" data-mp="2ypqfjxc8vx1p">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <p className="text-[1.0625rem] leading-[1.8] text-charcoal/70" data-id="element-239" data-mp="2kybi4lhlbtb4">
                      {step}
                    </p>
                  </div>
                </Reveal>)}
            </ol>

            <Reveal delay={0.1} data-id="element-240" data-mp="2b2i6r5fly5ux">
              <h2 className="eyebrow mt-16 text-mocha" data-id="element-241" data-mp="2g00a8gauvsvj">Outcomes</h2>
            </Reveal>
            <dl className="mt-8 grid gap-8 sm:grid-cols-3" data-id="element-242" data-mp="21zz70u74c9cw">
              {project.outcomes.map((o, i) => <Reveal key={o.label} delay={0.08 * i} data-id="element-243" data-mp="26yaj7bkvyvya" data-mpi={i}>
                  <div className="rounded-[1.25rem] border border-charcoal/[0.09] bg-ivorydeep/50 p-6" data-id="element-244" data-mp="270wp76s4p3ee">
                    <dd className="font-serif text-[2.5rem] font-light leading-none text-charcoal" data-id="element-245" data-mp="69t4hhdf1fx">
                      {o.value}
                    </dd>
                    <dt className="mt-3 text-xs uppercase tracking-[0.14em] text-charcoal/50" data-id="element-246" data-mp="fcfgigiv6e32">
                      {o.label}
                    </dt>
                  </div>
                </Reveal>)}
            </dl>

            {project.quote && <Reveal delay={0.1} data-id="element-247" data-mp="jd7q5avnekzv">
                <figure className="mt-20 border-l border-champagne pl-8" data-id="element-248" data-mp="jc63081cy81s">
                  <blockquote className="font-serif text-[1.75rem] font-light italic leading-[1.3] text-charcoal sm:text-[2.25rem]" data-id="element-249" data-mp="y1ca0i6ft2k8">
                    “{project.quote.text}”
                  </blockquote>
                  <figcaption className="mt-6 text-sm text-charcoal/55" data-id="element-250" data-mp="172tro4zhtcb1">
                    {project.quote.author}
                  </figcaption>
                </figure>
              </Reveal>}
          </div>
        </div>
      </section>

      <section className="border-t border-charcoal/[0.08] px-6 py-20 sm:px-10" data-id="element-251" data-mp="38vzrjc2pofz9">
        <div className="mx-auto max-w-[92rem]" data-id="element-252" data-mp="34twewk4b35ce">
          <Link to={`/work/${next.slug}`} className="group block" data-id="element-253" data-mp="17rxlfttsvnsh">
            <p className="eyebrow text-charcoal/40" data-id="element-254" data-mp="17ts7jbgt5ly5">Next case study</p>
            <div className="mt-6 flex items-end justify-between gap-8" data-id="element-255" data-mp="1crjz24djj61t">
              <h2 className="max-w-2xl font-serif text-[2rem] font-light leading-[1.08] text-charcoal transition-colors duration-500 group-hover:text-mocha sm:text-[3rem]" data-id="element-256" data-mp="1hplp599dw5ms">
                {next.title}
              </h2>
              <ArrowRightIcon className="mb-2 h-6 w-6 shrink-0 text-charcoal/40 transition-transform duration-500 ease-editorial group-hover:translate-x-2" strokeWidth={1.2} data-id="element-257" data-mp="1qps12qpfoxju" />
            </div>
          </Link>
        </div>
      </section>

      <CTASection data-id="element-258" data-mp="25eedoknofb7d" />
    </article>;
}